import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Button } from "./ui/button"

const prints = [
  {
    id: "mushroom-floor-lamps",
    title: "Mushroom Floor Lamps - Set of 2",
    artist: "Name Place Animal Thing ®",
    price: "₹60,800",
    image: "/StrInStill_1.png",
    wallView: "/1.png",
    sizes: ['16inch × 16inch × 62inch'],
    url: "/product-luxury/mushroom-floor-lamps-set-of-2-",
  },
  {
    id: "tanglin-wood-dining",
    title: "Tangline Wood Dining Table",
    artist: "Modern Lacquered Collection",
    price: "₹640,000",
    image: "/StrInStill_2.png",
    wallView: "/2.png",
    sizes: ['240cm × 120cm × 76cm'],
    url: "/product-luxury/tanglin-wood-dining-table",
  },
  {
    id: "vallamkali",
    title: "Vallamkali",
    artist: "Kerala Collection",
    price: "₹40,500",
    image: "/StrInStill_3.png",
    wallView: "/3.png",
    sizes: ['274cm × 183cm × 0cm'],
    url: "/product-luxury/vallamkali",
  },
  {
    id: "bedok-wood-sideboard",
    title: "Bedok Wood Sideboard",
    artist: "Modern Storage Collection",
    price: "₹660,000",
    image: "/StrInStill_4.png",
    wallView: "/4.png",
    sizes: ['180cm × 50cm × 70cm'],
    url: "/product-luxury/bedok-wood-sideboard-",
  },
  {
    id: "pratham-side-table",
    title: "Pratham Side Table",
    artist: "Contemporary Collection",
    price: "₹40,000",
    image: "/StrInStill_5.png",
    wallView: "/5.png",
    sizes: ['41cm × 41cm × 55cm'],
    url: "/product-luxury/pratham-side-table-4",
  },
  {
    id: "loopy-chair",
    title: "Loopy Chair",
    artist: "Contemporary Seating Collection",
    price: "₹79,900",
    image: "/StrInStill_6.png",
    wallView: "/6.png",
    sizes: ['1050cm × 800cm × 900cm'],
    url: "/product-luxury/loopy-chair-2",
  },
]

export default function ArtPrints() {
  const [viewMode, setViewMode] = useState("gallery")

  const handlePrintClick = (print) => {
    // Always redirect to product page
    window.location.href = print.url
  }

  return (
    <section className="bg-stone-50 py-24 dark:bg-stone-900">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12 text-center"
        >
          <h2 className="mb-4 font-serif text-3xl font-light md:text-4xl dark:text-white">Stories in Stillness</h2>
          <p className="mx-auto max-w-2xl text-muted-foreground">
            Fine art prints that transform spaces and evoke emotion.
          </p>
        </motion.div>

        <div className="mb-8 flex justify-center">
          <div className="inline-flex rounded-sm bg-stone-200 p-1 dark:bg-stone-800">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode("gallery")}
              className={viewMode === "gallery" ? "text-stone-800" : "text-stone-600"}
              style={viewMode === "gallery" ? { backgroundColor: '#f8e9d5' } : { backgroundColor: 'transparent' }}
            >
              Gallery View
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode("wall")}
              className={viewMode === "wall" ? "text-stone-800" : "text-stone-600"}
              style={viewMode === "wall" ? { backgroundColor: '#f8e9d5' } : { backgroundColor: 'transparent' }}
            >
              View On Wall
            </Button>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {viewMode === "gallery" ? (
            <motion.div
              key="gallery"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
            >
              {prints.map((print, index) => (
                <motion.div
                  key={print.id}
                  className="group cursor-pointer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -10 }}
                  onClick={() => handlePrintClick(print)}
                >
                  <div className="relative mb-4 overflow-hidden">
                    <div className="aspect-[3/4] bg-stone-100 dark:bg-stone-800">
                      <img
                        src={print.image || "/placeholder.svg"}
                        alt={print.title}
                        className="h-full w-full object-contain transition-transform duration-700 group-hover:scale-110"
                      />
                    </div>
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity"
                      whileHover={{ opacity: 1 }}
                    >
                      <p className="text-center text-white">View Product</p>
                    </motion.div>
                  </div>
                  <h3 className="font-serif text-lg text-black font-bold">{print.title}</h3>
                  <p className="text-sm text-muted-foreground">{print.artist}</p>
                  <p className="mt-1 font-light dark:text-stone-300">{print.price}</p>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="wall"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-1 gap-12"
            >
              {prints.map((print, index) => (
                <motion.div
                  key={print.id}
                  className="cursor-pointer"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  onClick={() => handlePrintClick(print)}
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={print.wallView || "/placeholder.svg"}
                      alt={`${print.title} on wall`}
                      className="h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    <div className="absolute bottom-0 left-0 p-6 text-white">
                      <h3 className="font-serif text-2xl text-white font-bold">{print.title}</h3>
                      <p className="text-sm opacity-80">{print.artist}</p>
                      <p className="mt-1">{print.price}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  )
} 